/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import Head from 'next/head';
import styled from 'styled-components';
import {TextButton} from '@calm-web/design-system';
import MDXLink from '@/components/terms/MarkdownUtils/MDXLink';
import {Columns, UpdatedDate, Wrapper} from '@/components/terms/styles';
export const meta = {
  title: 'Privacy Policy'
};
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    p: "p",
    strong: "strong",
    h2: "h2",
    h3: "h3",
    ul: "ul",
    li: "li",
    a: "a"
  }, _provideComponents(), props.components);
  return _jsxs(Wrapper, {
    children: [_jsx(_components.h1, {
      children: "Privacy Policy"
    }), _jsx(UpdatedDate, {
      children: "last updated December 12, 2024"
    }), _jsxs(_components.p, {
      children: ["Your privacy is of the utmost importance to us. This Privacy Policy explains how Calm.com, Inc. and our subsidiaries and affiliates (together, ", _jsx(_components.strong, {
        children: "\"Calm\""
      }), ", ", _jsx(_components.strong, {
        children: "\"we\""
      }), " or ", _jsx(_components.strong, {
        children: "\"us\""
      }), ") collect, use, and disclose information about you when you access or use our websites, mobile applications, and other online products and services (collectively, the “Services”), and when you contact our customer service team, engage with us on social media, or otherwise interact with us. Please note that certain of our products and services may be subject to a separate privacy policy. You will know this if those services link to a different notice rather than this policy."]
    }), _jsx(_components.p, {
      children: "Additional information for California residents, as well as for individuals in the EEA, UK, and Switzerland can be found at the end of this Privacy Policy."
    }), _jsx(_components.p, {
      children: "We may change this Privacy Policy from time to time. If we make changes, we will revise the date at the top of the policy and, in some cases, we may provide you with additional notice."
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "collection-of-information"
      }), "Collection of Information"]
    }), _jsx(_components.h3, {
      children: "Information You Provide to Us"
    }), _jsx(_components.p, {
      children: "We may collect information when you register for an account, participate in interactive features (like submitting a meditation rating or completing a check-in), fill out a form or a survey, participate in a contest or promotion, make a purchase, communicate with us via social media sites, request customer support, or otherwise interact with us. The information you provide may include:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Personal details"
        }), ": your name, email address, linked social media details, and street address."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Payment details"
        }), ": payment information."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Views and opinions"
        }), ": feedback, survey responses, and other information included within your interactions with us or otherwise provided via the Services. Some users also provide information about how they are feeling in connection with providing feedback or other messages to us, but we do not infer any health-related characteristics from this information and only process it consistent with the purpose for which it was provided."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Employment information"
        }), ": we may also collect employment information if you sign up through one of our special initiatives."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Other Information You May Provide"
        }), ": password, language settings, goals, previous meditation experience, sleep habits, and moods and reflections you provide during check-ins."]
      }), "\n"]
    }), _jsx(_components.h3, {
      children: "Information We Collect When You Use the Services or Interact with Us"
    }), _jsx(_components.p, {
      children: "When you use the Services or interact with us, we collect the following information about you:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Usage information"
        }), ": the sessions you use, videos you view, content you listen to, screens or features you access, and other similar types of usage information."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Transactional information"
        }), ": information about a purchase, such as product description, price, subscription or free trial expiration date, and time and date of the transaction."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Log information"
        }), ": the web browser you use, app version, access times and dates, pages viewed, your IP address, and the page you visited before navigating to our websites."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Device information"
        }), ": information about the computer or mobile device you use to access the Services, including the hardware model, operating system and version, device identifiers set by your device operating system, and mobile network information."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Communications"
        }), ": we may record our communications including chat messages, phone, or video calls, such as when you utilize our chat-bot or provide us with feedback or market research."]
      }), "\n"]
    }), _jsx(_components.h3, {
      children: "Information We Infer or Generate When You Use the Services"
    }), _jsx(_components.p, {
      children: "When you use the Services, we infer or generate the following information about you:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "User ID"
        }), ": a user ID that we will associate with your account."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Derived information"
        }), ": information about you based on other information we have collected. For example, like most platforms, we use your IP address to derive the approximate location of your device. We may make educated guesses about your gender or age. We may also use information we collect about you to help determine the likelihood of you continuing to use the Services in the future."]
      }), "\n"]
    }), _jsx(_components.h3, {
      children: "Information We Collect from Other Sources"
    }), _jsx(_components.p, {
      children: "We may also obtain information about you from other sources, including:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Transaction information"
        }), ": details from third-parties you use to install our app or purchase a subscription."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Calendar information"
        }), ": details from third-party calendar services that you choose to integrate with our services."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Social media data"
        }), ": if you create or log into your Calm account through a social media service account, we will have access to information from that account, such as your name and other account information, in accordance with your data sharing settings on that social media service."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Third party health app data"
        }), ": with your permission, we may also receive data from your mobile device’s health app (like Apple HealthKit or Google Health Connect), including hours of sleep and sleep goals. However, we do not infer any health-related characteristics from this information and only process it consistent with the purpose for which it was originally provided."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsxs("u", {
          children: [_jsx(MDXLink, {
            text: "Cookie",
            href: "https://www.calm.com/cookies",
            label: "Cookie"
          }), " data"]
        }), ": we may collect information via cookies and web beacons."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx("u", {
          children: "Information you make public"
        }), ": finally, we may obtain information you have made publicly available, including from websites and online services you use, consumer research platforms, and/or business contact databases."]
      }), "\n"]
    }), _jsx(_components.h2, {
      children: "Use of Information"
    }), _jsx(_components.p, {
      children: "We use the information we collect as follows:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Provide and maintain the Services, including debugging to identify and repair errors (the legal basis for this processing is the performance of the user agreement between you and Calm);"
      }), "\n", _jsx(_components.li, {
        children: "Improve the Services, including by developing new products and services (the legal basis for this processing is our legitimate interest in improving and developing new services, by exploring ways to further enhance our services and business);"
      }), "\n", _jsx(_components.li, {
        children: "Process transactions and fulfill orders (the legal basis for this processing is the performance of the user agreement between you and Calm);"
      }), "\n", _jsx(_components.li, {
        children: "Send you transactional or relationship messages, such as receipts, account notifications, customer service responses, and other administrative messages (the legal basis for this processing is our legitimate interest in providing relevant information about our services, including your purchases from us);"
      }), "\n", _jsx(_components.li, {
        children: "Communicate with you about products, services, and events offered by Calm and others, request feedback, and send news, gifts, or other information we think will be of interest to you (see the “Your Choices” section below for information on how to opt out of marketing messages) (The legal basis for this processing is our legitimate interest in providing information about products and services that may be of interest to you unless applicable law requires us to obtain your consent, in which case we will do so);"
      }), "\n", _jsx(_components.li, {
        children: "Monitor and analyze trends, usage, and activities in connection with the Services (the legal basis for this processing is our legitimate interest in improving our Services and understanding our users’ needs and expectations);"
      }), "\n", _jsx(_components.li, {
        children: "Detect, investigate, and prevent fraudulent transactions and other illegal activities and protect the rights and property of Calm and others, including to enforce our agreements and policies (the legal basis for this processing is our legitimate interest in preventing fraud and protecting and securing our assets, customers, employees and the public);"
      }), "\n", _jsx(_components.li, {
        children: "Comply with the law, such as by processing transactional records for tax filings and other compliance activities (the legal basis for this processing is compliance with our legal obligations under applicable law related to, for instance, taxation, and consumer protection law);"
      }), "\n", _jsx(_components.li, {
        children: "Create anonymous or aggregated data that no longer can be reasonably used to identify you (the legal basis for this processing is our legitimate interest in creating non-personally identifiable data to help improve our services, provide reports to third parties, and otherwise enhance and promote our business);"
      }), "\n", _jsx(_components.li, {
        children: "Personalize your online experience and the advertisements you see on other platforms based on your preferences, interests, and browsing behavior (the legal basis for this processing is our legitimate interest in improving your experience with the Services and serving advertisements more relevant to your interests, unless applicable law requires us to obtain your consent, in which case we will do so); and"
      }), "\n", _jsx(_components.li, {
        children: "Facilitate contests, sweepstakes, and promotions (the legal basis for this processing is our legitimate interest in conducting promotional activities that our users may voluntarily decide to participate in)."
      }), "\n"]
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "disclosure-of-information"
      }), "Disclosures of Information"]
    }), _jsx(_components.p, {
      children: "We may disclose information about you as follows and as otherwise described in this Privacy Policy or at the time of collection:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "With companies and contractors that perform services for us, including email service providers, payment processors, fraud prevention vendors, analytics providers, advertising partners, and other service providers;"
      }), "\n", _jsx(_components.li, {
        children: "To accountants, auditors, lawyers, and other outside professional advisors to Calm, subject to appropriate contractual obligations of confidentiality;"
      }), "\n", _jsx(_components.li, {
        children: "If we believe disclosure is in accordance with, or required by, applicable law or legal process, including court order, subpoena, or other lawful requests by public authorities to meet national security or law enforcement requirements;"
      }), "\n", _jsx(_components.li, {
        children: "If we believe your actions are inconsistent with our user agreements or policies, if we believe you have violated the law, where necessary for the purposes of prevention, investigation, detection or prosecution of criminal offenses or the execution of criminal penalties, or to protect the rights, property, and safety of Calm or others, or if it is necessary for the establishment, exercise or defense of legal claims;"
      }), "\n", _jsx(_components.li, {
        children: "In connection with any merger, sale of company assets, financing or acquisition of all or a portion of our business by another company;"
      }), "\n", _jsx(_components.li, {
        children: "Between and among Calm and our current and future parents, affiliates, subsidiaries, and other companies under common control and ownership;"
      }), "\n", _jsx(_components.li, {
        children: "If your Calm subscription has been provided to you by someone else, like your employer or a family member who invited you to use one of their dependent subscriptions, we may inform them that you have signed up for the subscription they offered you;"
      }), "\n", _jsx(_components.li, {
        children: "If your Calm subscription was obtained through a third-party promotion, such as bundled with a third-party service or offered through a promotional code distributed by that third party, we may inform them that you redeemed the offer; and"
      }), "\n", _jsx(_components.li, {
        children: "With your consent or at your direction. For instance, you may choose to share actions you’ve taken through the Services using “share” or similar features or by linking your Calm account with third-party services."
      }), "\n"]
    }), _jsx(_components.p, {
      children: "We may also disclose aggregated or other information not subject to obligations under the data protection laws of your jurisdiction with third parties."
    }), _jsx(_components.h2, {
      children: "Advertising and Analytics Services Provided by Others"
    }), _jsx(_components.p, {
      children: "We allow others to provide analytics services and serve advertisements on our behalf across the web and other online services. These entities use cookies, web beacons, device identifiers, and other technologies to collect information about your use of the Services and other websites and online services, including your IP address, device identifiers, web browser, mobile network information, pages viewed, time spent on pages or in apps, links clicked, and conversion information. Calm and others may use this information to, among other things, analyze and track data, determine the popularity of certain content, deliver advertising and content targeted to your interests, and better understand your online activity. Some of our advertising partners enable us to convert your email address or phone number into an identifier to show ads that are more relevant to you on other platforms."
    }), _jsxs(_components.p, {
      children: ["Depending on where you are located, you can disable cookies used for advertising purposes by visiting ", _jsx(MDXLink, {
        text: "calm.com/optout",
        href: "https://calm.com/optout",
        label: "calm.com/optout"
      }), " or through our ", _jsx(TextButton, {
        onClick: props.onOptOutClick,
        children: "Cookie Preferences Manager"
      }), ". Or, for more information about interest-based ads, including to use ad industry tools to opt out of having your web browsing information used for behavioral advertising purposes, please visit ", _jsx(MDXLink, {
        text: "www.aboutads.info/choices",
        href: "http://www.aboutads.info/choices",
        label: "www.aboutads.info/choices"
      }), " (if you are in the EU, please visit ", _jsx(MDXLink, {
        text: "www.youronlinechoices.eu/",
        href: "http://www.youronlinechoices.eu/",
        label: "www.youronlinechoices.eu/"
      }), "). Your mobile device should also include a feature that allows you to opt out of having certain information collected through apps used for behavioral advertising purposes."]
    }), _jsx(_components.h2, {
      children: "Data Transfer"
    }), _jsxs(_components.p, {
      children: ["Calm is based in the United States and has operations there and in other countries. As a result, we and our service providers process information in countries which may not provide equivalent levels of data protection as your home jurisdiction. Where required by law, we provide adequate protection for the transfer of personal data in accordance with applicable law, such as by obtaining your consent, relying on the European Commission’s adequacy decisions, or executing Standard Contractual Clauses. Where relevant, you may request a copy of these Standard Contractual Clauses by emailing us at ", _jsx(MDXLink, {
        text: "support@calm.com",
        href: "mailto:support@calm.com",
        label: "support@calm.com"
      }), "."]
    }), _jsx(_components.h2, {
      children: "Privacy Rights"
    }), _jsxs(_components.p, {
      children: ["You may update certain account information (such as your password, name, and email address) by logging into your account, contacting us through our ", _jsx(MDXLink, {
        text: "Help Center",
        href: "https://support.calm.com/hc/en-us",
        label: "Help Center"
      }), ", or emailing us at ", _jsx(MDXLink, {
        text: "support@calm.com",
        href: "mailto:support@calm.com",
        label: "support@calm.com"
      }), "."]
    }), _jsx(_components.p, {
      children: "Regardless of your location, but subject to certain limits and conditions provided under law, you have the right to request to:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "know more about the information we have about you;"
      }), "\n", _jsx(_components.li, {
        children: "access information we have about you (including in a portable format);"
      }), "\n", _jsx(_components.li, {
        children: "correct information we have about you;"
      }), "\n", _jsx(_components.li, {
        children: "opt out of “sales”, targeted advertising, or “sharing”; and"
      }), "\n", _jsx(_components.li, {
        children: "delete the information we have about you."
      }), "\n"]
    }), _jsx(_components.p, {
      children: "Where relevant, you also have the right to limit sensitive personal data processing or opt out of profiling for decisions that produce legal or similarly significant effects. However, Calm does not currently engage in processing that gives rise to those opt-out rights."
    }), _jsx(_components.p, {
      children: "If you request to delete your information, please note that we retain certain information when required or permitted by law."
    }), _jsx(_components.h3, {
      children: "Exercising Your Rights"
    }), _jsxs(_components.p, {
      children: ["You can exercise these rights by emailing us at ", _jsx(MDXLink, {
        text: "support@calm.com",
        href: "mailto:support@calm.com",
        label: "support@calm.com"
      }), " or by submitting the request on our website at ", _jsx(MDXLink, {
        text: "calm.com/contact",
        href: "https://support.calm.com/hc/en-us/requests/new",
        label: "calm.com/contact"
      }), " (click on the box that says “Ask a question” and type your request into the pop-up window). We will not provide discriminatory treatment against anyone that exercises any of their rights."]
    }), _jsxs(_components.p, {
      children: ["Depending on where you are located, you can opt out of use of data collected via the Services for targeted advertising, and related “sales” and “sharing,” by visiting ", _jsx(MDXLink, {
        text: "calm.com/optout",
        href: "https://calm.com/optout",
        label: "calm.com/optout"
      }), ", using our ", _jsx(TextButton, {
        onClick: props.onOptOutClick,
        children: "Cookie Preferences Manager"
      }), " to disable ad trackers on our website, or by visiting our websites with a recognized universal choice signal enabled (such as the Global Privacy Control). On our mobile app, you may adjust your mobile device settings to limit ad tracking (iOS: Settings &gt Privacy & Security &gt Tracking &gt Allow Apps to Request to Track; Android: Settings &gt Google &gt Ads &gt Opt out of Ads Personalization).  . Please note that, depending on which opt-out preference signal you use, our processing of the signal may be limited to the specific browser or device that you are using. You may need to renew your opt-out if you use a different browser or device to access the Services."]
    }), _jsx(_components.h3, {
      children: "Verification"
    }), _jsx(_components.p, {
      children: "Where appropriate under applicable law, such as with respect to requests to know, correct, or delete, we may verify your request by asking you to provide information that matches information we have on file."
    }), _jsx(_components.p, {
      children: "You may designate an authorized agent to exercise any of the rights set out in this Privacy Policy on your behalf. Authorized agents should submit requests through the same channels, but we may require proof that the person is authorized to act on your behalf and may also still ask you to verify your identity with us directly."
    }), _jsx(_components.h3, {
      children: "Appeals"
    }), _jsxs(_components.p, {
      children: ["If we deny your request, you may appeal our decision by emailing ", _jsx(MDXLink, {
        text: "support@calm.com",
        href: "mailto:support@calm.com",
        label: "support@calm.com"
      }), ". If you are in the U.S. and have concerns about the result of the appeal, you may contact the attorney general in the state where you reside."]
    }), _jsx(_components.h2, {
      children: "Other Choices"
    }), _jsx(_components.h3, {
      children: "Promotional Communications"
    }), _jsx(_components.p, {
      children: "You may opt out of receiving promotional emails from Calm by following the instructions in those emails or by logging into your account and managing your contact preferences. If you opt out, we may still send you non-promotional emails, such as those about your account or our ongoing business relations."
    }), _jsx(_components.h3, {
      children: "Mobile Push Notifications/Alerts"
    }), _jsx(_components.p, {
      children: "With your consent, we send promotional and other push notifications to your mobile device. You can deactivate these messages at any time by changing the notification settings on your mobile device."
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "information-for-california-residents"
      }), "Information for California Residents"]
    }), _jsx(_components.p, {
      children: "This section provides additional disclosures required by the California Consumer Privacy Act, as amended."
    }), _jsx(_components.p, {
      children: "Please see the chart below for a list of the personal information we have collected about California consumers in the last twelve (12) months, along with our business and commercial processing purposes and categories of third parties to whom this information may be disclosed. For more details about the personal information we collect, including the categories of sources, please see the Collection of Information section above."
    }), _jsxs(Columns, {
      children: [_jsxs("div", {
        children: [_jsx(_components.h3, {
          children: "Categories of personal information we collect"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Identifiers"
          }), ", such as your name, phone number, email address, social media handle, and unique identifiers (like IP address) tied to your browser or device.\n", _jsx(_components.strong, {
            children: "Characteristics of protected classifications under state or federal law"
          }), ", such as gender and age.\n", _jsx(_components.strong, {
            children: "Commercial information"
          }), ", such as your payment information and Calm product or service purchases.\n", _jsx(_components.strong, {
            children: "Approximate geolocation data"
          }), ".\n", _jsx(_components.strong, {
            children: "Internet or other electronic network activity"
          }), ", such as browsing behavior and information about your usage and interactions with the Services.\n", _jsx(_components.strong, {
            children: "Audio, electronic, visual, or similar information"
          }), ", such as profile photo or personal information you may provide during customer support calls and call recordings.\n", _jsx(_components.strong, {
            children: "Professional, employment, and education information"
          }), ", such as information about your employer or professional background that you voluntarily provide to us.\n", _jsx(_components.strong, {
            children: "Other personal information you provide"
          }), ", including opinions, preferences, goals, and previous meditation experience and other personal information contained in product reviews, surveys, or communications.\n", _jsx(_components.strong, {
            children: "Inferences"
          }), " drawn from the above, such as product interests and purchasing insights."]
        })]
      }), _jsxs("div", {
        children: [_jsx(_components.h3, {
          children: "Business or commercial purposes for which we may use your information"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Perform or provide the services"
          }), ", such as to maintain accounts, provide customer service, process orders and transactions, and verify customer information.\n", _jsx(_components.strong, {
            children: "Improve and maintain the Services"
          }), ", such as by improving the Services and developing new products and services.\n", _jsx(_components.strong, {
            children: "Debug"
          }), ", such as to identify and repair errors and other functionality issues.\n", _jsx(_components.strong, {
            children: "Communicate with you"
          }), " about marketing and other relationship or transactional messages.\n", _jsx(_components.strong, {
            children: "Analyze usage"
          }), ", such as by monitoring trends and activities in connection with use of the Services.\n", _jsx(_components.strong, {
            children: "Personalize your online experience"
          }), ", such as by tailoring the content and ads you see on the Services and on other platforms based on your preferences, interests, and browsing behavior.\n", _jsx(_components.strong, {
            children: "Legal reasons"
          }), ", such as to help detect and protect against security incidents, or other malicious, deceptive, fraudulent, or illegal activity."]
        })]
      }), _jsxs("div", {
        children: [_jsx(_components.h3, {
          children: "Parties with whom information may be shared"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Companies that provide services to us"
          }), ", such as those that assist us with customer support, subscription and order fulfillment, advertising measurement, communications and surveys, data analytics, fraud prevention, cloud storage, bug fix management and logging, and payment processing.\n", _jsx(_components.strong, {
            children: "Third parties with whom you consent to sharing your information"
          }), ", such as with social media services or academic researchers.\n", _jsx(_components.strong, {
            children: "Government entities or other third parties for legal reasons"
          }), ", such as to comply with law or for other legal reasons as described in our ", _jsx(_components.a, {
            href: "#disclosure-of-information",
            children: "Disclosure section"
          }), "."]
        })]
      })]
    }), _jsxs(_components.p, {
      children: ["California consumers have the rights described above under the Privacy Rights section. We use cookies and similar tracking technologies, and we allow our advertising partners to use such technologies, so we can, among other things, show you ads promoting Calm on other websites and services. These activities may be considered “sales,” “sharing,” or “targeted advertising” under certain privacy laws. You can opt out of these practices by visiting ", _jsx(MDXLink, {
        text: "calm.com/optout",
        href: "https://calm.com/optout",
        label: "calm.com/optout"
      }), ". We do not knowingly “sell” or “share” personal data about consumers under the age of 16."]
    }), _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Notice of Financial Incentives"
      }), ": We offer various financial incentives. For example, we may provide incentives to customers who participate in a survey or provide testimonials. When you participate in a financial incentive, we collect personal information from you, such as identifiers (like your name and email address) and information about your experiences using the Services. You can opt into a financial incentive by following the sign-up or participation instructions provided, and, for any ongoing benefits, you can opt out at any time, such as by following the unsubscribe instructions in the applicable program’s terms or by contacting us. In some cases, we may provide additional terms and conditions for a financial incentive, which we will provide to you when you sign up. The value of your personal information is reasonably related to the value of the offer or discount presented to you."]
    }), _jsx(_components.p, {
      children: "We retain personal data for no longer than is necessary for the purposes for which it is processed, unless applicable law requires storage for a longer period of time."
    }), _jsx(_components.h2, {
      children: "Information for Individuals in EEA, Switzerland, and UK"
    }), _jsxs(_components.p, {
      children: ["The sections below apply to you if you use the Services while in the European Economic Area, Switzerland, or the United Kingdom (collectively, ", _jsx(_components.strong, {
        children: "“Europe”"
      }), "). Calm.com, Inc. is the data controller for personal data governed by this Privacy Policy."]
    }), _jsx(_components.h3, {
      children: "Data Retention"
    }), _jsx(_components.p, {
      children: "We retain personal data for no longer than is necessary for the purposes for which it is processed, unless applicable law requires storage for a longer period of time."
    }), _jsx(_components.h3, {
      children: "Data Subject Requests"
    }), _jsx(_components.p, {
      children: "Subject to certain limits and conditions provided under law, in addition to the rights described under the Privacy Rights section above, you have the right to:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Object to certain processing (like receiving direct marketing), or request that we restrict processing in certain circumstances (like to retain but not further process pending resolution of a claim)."
      }), "\n", _jsx(_components.li, {
        children: "Withdraw any consent you have provided."
      }), "\n", _jsx(_components.li, {
        children: "Request that Calm transfer certain data to another data controller."
      }), "\n", _jsxs(_components.li, {
        children: ["File a complaint regarding our data protection practices with a supervisory authority.", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: ["Please see this directory for contact details:  ", _jsx(MDXLink, {
              text: "https://edpb.europa.eu/about-edpb/board/members_en",
              href: "https://edpb.europa.eu/about-edpb/board/members_en",
              label: "https://edpb.europa.eu/about-edpb/board/members_en"
            }), "."]
          }), "\n", _jsxs(_components.li, {
            children: ["If you are in Switzerland, please visit this FDPIC site for contact details:  ", _jsx(MDXLink, {
              text: "https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html",
              href: "https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html",
              label: "https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html"
            }), "."]
          }), "\n", _jsxs(_components.li, {
            children: ["If you are in the United Kingdom, please see this site for contact details:  ", _jsx(MDXLink, {
              text: "https://ico.org.uk/global/contact-us/",
              href: "https://ico.org.uk/global/contact-us/",
              label: "https://ico.org.uk/global/contact-us/"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n"]
    }), _jsx(_components.p, {
      children: "If you would like to exercise any of these rights and can't do so directly via the Services or your device, you may contact us as indicated below."
    }), _jsx(_components.h2, {
      children: "Contact Us"
    }), _jsxs(_components.p, {
      children: ["If you have any questions about this Privacy Policy, please contact Calm by email at ", _jsx(MDXLink, {
        text: "support@calm.com",
        href: "mailto:support@calm.com",
        label: "support@calm.com"
      }), "."]
    }), _jsx(_components.p, {
      children: "If you are in Europe, you may also contact our representative at:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "EEA"
          }), " : The DPO Centre Europe Ltd, Alexandra House, 3 Ballsbridge Park, Dublin, D04C 7H2 - (+353 1 631 9460) (", _jsx(MDXLink, {
            text: "EURep@calm.com",
            href: "mailto:EURep@calm.com",
            label: "EURep@calm.com"
          }), ")."]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "UK"
          }), " : The DPO Centre 50 Liverpool Street London, EC2M 7PY - (+44 (0) 203 797 1289) (", _jsx(MDXLink, {
            text: "ukrep@calm.com",
            href: "mailto:ukrep@calm.com",
            label: "ukrep@calm.com"
          }), ")."]
        }), "\n"]
      }), "\n"]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
